<template>
  <div class="event-categories" redesign>
    <!-- Request stepper -->
    <Stepper
      stepInfo="Step 2 of 5"
      description="Describe your situation"
    />

    <div class="event-categories-form form-container">
      <!-- Information -->
      <div v-if="content" class="form-header">
        <span class="tooltip-text" v-html="content" />
      </div>

      <!-- Loading spinner -->
      <div v-if="globalLoading" class="form-field">
        <LoadSpinner :color="`#${organizationDetails.darkBrandColor}`" />
      </div>

      <!-- Event categories -->
      <div
        v-if="!globalLoading"
        v-for="(eventCategory, i) in selectableEventCategories"
        :key="i"
        class="form-field card-shape"
      >
        <EventCategoryComponent
          :eventCategory="eventCategory"
          :selected="selectedEventCategoryIds.includes(eventCategory.id)"
          @selected="addEventCategory"
          @unselected="removeEventCategory"
        />
      </div>

      <!-- Other category -->
      <div v-if="!globalLoading && otherCategory" class="form-field card-shape">
        <EventCategoryComponent
          :eventCategory="otherCategory"
          :selected="selectedEventCategoryIds.includes(otherCategory.id)"
          @selected="addEventCategory"
          @unselected="removeEventCategory"
        />
      </div>

      <!-- COVID error -->
      <div v-if="displayCovidError" class="form-field error-container">
        <div class="tooltip-icon">
          <img src="@/assets/icons/error-icon.png" />
        </div>
        <div class="tooltip">
          <h4 class="tooltip-header" v-translate>Additional emergency required</h4>
          <span class="tooltip-text" v-translate>
            Please select an additional emergency for COVID
          </span>
        </div>
      </div>

      <!-- No emergency error -->
      <div v-if="displaySelectEmergencyError" class="form-field error-container">
        <div class="tooltip-icon">
          <img src="@/assets/icons/error-icon.png" />
        </div>
        <div class="tooltip">
          <h4 class="tooltip-header" v-translate>No emergency selected</h4>
          <span class="tooltip-text" v-translate>
            Please select at least one emergency type
          </span>
        </div>
      </div>

      <!-- Next step -->
      <div class="form-footer">
        <div class="next-step">
          <translate>Next step:</translate><br /><translate class="next-step-name"
            >Add details</translate
          >
        </div>
      </div>


      <!-- Submit -->
      <SubmitButton :loading="loading" :onContinue="onContinue" />
    </div>

    <Modal
      :additionalModalStyles="{
        width: '100%',
        'max-width': '500px',
        border: 'none',
        overflow: 'hidden',
      }"
      v-if="naturalDisasterModalOpen"
      dimmed
    >
      <div class="form-container card-shape modal-window">
        <div class="form-field">
          <div class="fcolumn">
            <div class="frow" style="padding-bottom: 1rem">
              <p
                v-translate
              >
              Your organization hasn’t turned on the 'Natural Disaster' category in your area.  If an event has damaged your property, or your person, please select the most applicable of our other categories instead. &#10;&#13;
              </p>
            </div>
            <div class="frow" style="padding-bottom: 1rem">
              <p
                v-translate
              >
              If a natural disaster is affecting you and your coworkers, let us know so we can help your organization turn on the 'Natural Disaster' category.
              </p>
            </div>
          </div>
        </div>

        <div class="button-container">
          <button @click="naturalDisasterModalOpen = false" class="form-button">
            <span v-translate>Continue</span>
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import EventCategoryComponent from '@/components/requests/EventCategoryComponent'
import InfoBox from '@/components/InfoBox'
import Request from '@/services/requests'
import EventCategory from '@/services/eventCategories'
import LoadSpinner from '@/components/LoadSpinner'
import { mapState } from 'vuex'
import { analyticsMixin } from '@/services/googleAnalytics'
import RequestStepProgressBar from '@/components/stepper/RequestStepProgressBar'
import SubmitButton from '@/components/requests/SubmitButton'
import Modal from '@/components/Modal'
import Stepper from '@/components/requests/Stepper'

export default {
  name: 'EventCategories',
  mixins: [analyticsMixin],
  components: {
    EventCategoryComponent,
    LoadSpinner,
    InfoBox,
    RequestStepProgressBar,
    Modal,
    SubmitButton,
    Stepper,
  },
  data() {
    return {
      eventCategories: [],
      selectedEventCategoryIds: [],
      loading: false,
      globalLoading: false,
      displayCovidError: false,
      displaySelectEmergencyError: false,
      naturalDisasterModalOpen: false,
      content: this.$gettext(
        'Please select the type of event that requires financial assistance.',
      ),
    }
  },
  async created() {
    this.globalLoading = true
    // Pull all Event Categories to populate this page
    const response = await EventCategory.api.list({
      filters: { event_category_organization: this.currentRequest.organization },
    })
    this.eventCategories = response.results
    // Auto-select all Event Categories that have been previously selected for this request
    this.selectedEventCategoryIds = this.currentRequest.eventCategories
    // this.startEditingPing()
    this.globalLoading = false
  },
  destroyed() {
    clearInterval(this.interval)
  },
  methods: {
    async onContinue() {
      this.loading = true
      this.trackClickedCTA()
      /**
       * checking for id of COVID and if the number of events selected is equal to 1
       * if this is true an error will be thrown
       */
      if (
        this.selectedEventCategoryIds.includes(this.getCOVIDId()) &&
        this.selectedEventCategoryIds.length === 1
      ) {
        // display error for CORONAVIRUS
        this.loading = false
        this.displayCovidError = true
        this.eventsTracker('covid-error', 'Please select more than 1 event for COVID')
        return Promise.reject('covid-error')
      } else if (this.selectedEventCategoryIds.length == 0) {
        // Display error for no emergency selected
        this.loading = false
        this.displaySelectEmergencyError = true
        return Promise.reject('select-emergency-error')
      } else {
        // Build an updated Request object, push the update to the API, update state, move on
        const request = Request.create({
          ...this.currentRequest,
          eventCategories: this.selectedEventCategoryIds,
        })
        const updateFields = ['eventCategories']
        const response = await Request.api.update(request, updateFields)
        this.$store.dispatch('updateCurrentRequest', response)
      }
    },
    addEventCategory(eventCategory) {
      const val = eventCategory.id
      this.displaySelectEmergencyError = false
      this.eventsTracker('selected-event-category', `Category ID: ${val}`)
      // User not allowed to select both "Other" and a different category - so first remove Other
      this.removeOtherFromSelected()
      if (this.organizationDetails.selectOneEvent) {
        this.selectedEventCategoryIds.forEach(categoryId => {
          const removeThisCategory = this.eventCategories.find(category => category.id === categoryId)
          this.removeEventCategory(removeThisCategory)
        })
      }
      this.selectedEventCategoryIds.push(val)
    },
    async removeEventCategory(eventCategory) {
      const val = eventCategory.id
      this.eventsTracker('deselected-event-category', `Category ID: ${val}`)
      this.selectedEventCategoryIds = this.selectedEventCategoryIds.filter(id => id != val)

      this.currentRequest.eventsRef.forEach(async oldEvent => {
        if (oldEvent.categoryRef.id == val) {
          await this.$store.dispatch('partialUpdateEvent', { id: oldEvent.id, removeEvent: true })
        }
      })
    },
    // reducer function to get the id of COVID-19
    getCOVIDId() {
      const value = this.eventCategories.reduce((returnedValue, event) => {
        if (event.name === EventCategory.options.COVID19) {
          returnedValue = event.id
          return returnedValue
        } else {
          return returnedValue
        }
      }, '')
      return value
    },
    onOtherCategorySelected() {
      this.eventsTracker('selected-event-category', `Category ID: ${this.otherCategory.id}`)
      this.selectedEventCategoryIds = [this.otherCategory.id]
      this.onContinue()
    },
    removeOtherFromSelected() {
      if (this.otherCategory && this.selectedEventCategoryIds.includes(this.otherCategory.id)) {
        this.eventsTracker('deselected-event-category', `Category ID: ${this.otherCategory.id}`)
        this.selectedEventCategoryIds = this.selectedEventCategoryIds.filter(
          id => id !== this.otherCategory.id,
        )
      }
    },
    startEditingPing() {
      const request = this.currentRequest
      Request.api.editingPing(request)
      this.interval = setInterval(function() {
        Request.api.editingPing(request)
      }, 10000)
    },
  },
  computed: {
    ...mapState(['user', 'currentRequest', 'organizationDetails']),
    disabled() {
      // Disable button if user hasn't selected any categories, or if page is loading
      if (this.selectedEventCategoryIds.length == 0 || this.loading) {
        return true
      }
      return false
    },
    /* Return all selectable event categories - i.e. all except the public category "Other". */
    selectableEventCategories() {
      const allButOther = this.eventCategories.filter(
        eC => eC.name.toLowerCase() !== 'other' && eC.name.toLowerCase() !== 'otra',
      )
      return allButOther
    },
    /* Return public event category "Other", if it is available for this organization; otherwise return null. */
    otherCategory() {
      const otherCategory = this.eventCategories.find(
        eC => eC.name.toLowerCase() === 'other' || eC.name.toLowerCase() === 'otra',
      )
      return otherCategory ? otherCategory : null
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';

.event-categories {
  display: flex;
  flex-direction: column;

  .event-categories-form {
    @extend .constrain-tablet-and-up;
  }
}
</style>
